import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Color, Group, Object3D, Object3DEventMap, Vector2, Vector3 } from 'three'
import { useTheme } from '@mui/material'
import { GltfModel, useCanvasListener } from '@modugen/ui-toolkit'
import { useGLTF } from '@react-three/drei'
import { ThreeEvent } from '@react-three/fiber'
import { useSceneStore } from '@editorStores'

const userData = {
  type: 'gltf-model',
}

/** Quaternion [x, y, z, w] for model rotation. */
export type ModelRotation = [number, number, number, number]

/** Pipeline: model.translate(t1).rotate(R).translate(t2) → world = t2 + R*(local + t1). */
export interface AlignmentTransform {
  PIFCRef: [number, number, number]
  PModelRef: [number, number, number]
  R: ModelRotation
}

interface GlbModelViewerProps {
  glbUrl: string
  guidToHidden: Record<string, boolean>
  guidToColor: Record<string, Color>
  selectedIfcElementGuids: string[]
  onElementClick?: (guid: string, event: ThreeEvent<MouseEvent>) => void
  onContextMenuElement?: (guid: string, event: ThreeEvent<MouseEvent>) => void
  onLoad?: (
    gltfModel: Group<Object3DEventMap>,
    nodes: Record<string, Object3D<Object3DEventMap>>,
  ) => void
  /** When set, applies translate(t2).rotate(R).translate(t1) to the model (IFC alignment). */
  alignmentTransform?: AlignmentTransform | null
  opacity?: number
  enableHover?: boolean
}

const GlbModelViewer = ({
  glbUrl,
  guidToHidden,
  guidToColor,
  selectedIfcElementGuids,
  onElementClick,
  onContextMenuElement,
  onLoad,
  alignmentTransform,
  opacity,
  enableHover = true,
}: GlbModelViewerProps) => {
  const theme = useTheme()
  const { scene: gltfModel, nodes } = useGLTF(glbUrl)
  const [hoveredGuid, setHoveredGuid] = useState<string | null>(null)
  const clipPlanes = useSceneStore(state => state.gltfClipPlanes)

  const mergedGuidToColor = useMemo(() => {
    const selectionColor = new Color(theme.scenePalette.selection as string)
    const hoverColor = new Color(theme.scenePalette.hover as string)
    const base = { ...guidToColor }
    if (hoveredGuid && enableHover) base[hoveredGuid] = hoverColor
    selectedIfcElementGuids.forEach(guid => {
      base[guid] = selectionColor
    })
    return base
  }, [
    enableHover,
    guidToColor,
    hoveredGuid,
    selectedIfcElementGuids,
    theme.scenePalette.selection,
    theme.scenePalette.hover,
  ])

  const onPointerOver = useCallback((guid: string, event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation()
    setHoveredGuid(guid)
  }, [])

  const onPointerOut = useCallback((id: string, event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation()
    setHoveredGuid(null)
  }, [])

  useEffect(() => {
    onLoad?.(gltfModel, nodes)
  }, [onLoad, gltfModel, nodes])

  const transform = useMemo(() => {
    if (!alignmentTransform) return null

    const { PIFCRef, PModelRef, R } = alignmentTransform
    const t1 = new Vector3(PIFCRef[0], PIFCRef[1], PIFCRef[2]).multiplyScalar(-1)
    const t2 = new Vector3(PModelRef[0], PModelRef[1], PModelRef[2])

    return { t1, t2, R }
  }, [alignmentTransform])

  const startPosition = useRef<Vector2>(new Vector2())
  const currentPosition = useRef<Vector2>(new Vector2())
  useCanvasListener('pointerdown', event => {
    startPosition.current = new Vector2(event.clientX, event.clientY)
  })
  useCanvasListener('mousemove', event => {
    currentPosition.current = new Vector2(event.clientX, event.clientY)
  })

  const handlePointerUp = (guid: string, event: ThreeEvent<MouseEvent>) => {
    if (event.button !== 2) return
    if (startPosition.current.distanceTo(currentPosition.current) > 10) return

    onContextMenuElement?.(guid, event)
  }

  if (!gltfModel || !nodes) {
    return null
  }

  return (
    <group key={transform?.toString()} position={transform?.t2}>
      <group quaternion={transform?.R}>
        <group position={transform?.t1}>
          <GltfModel
            model={gltfModel}
            nodes={nodes}
            guidToHidden={guidToHidden}
            guidToColor={mergedGuidToColor}
            onClick={onElementClick}
            onPointerUp={onContextMenuElement ? handlePointerUp : undefined}
            onPointerOver={enableHover ? onPointerOver : undefined}
            onPointerOut={enableHover ? onPointerOut : undefined}
            opacity={opacity}
            additionalUserData={userData}
            // The clip plane fix results in a weird flickering sometimes,
            // so i only want it to be applied when there are clip planes applied
            useRaycastClipPlaneFix={!!clipPlanes.length}
            clipPlanes={clipPlanes}
          />
        </group>
      </group>
    </group>
  )
}

export default GlbModelViewer
